<template>
  <Modal
    v-model="visible"
    class-name="upload-modal--create"
    :title="$t('upload.createFile')"
    :width="480"
    :mask-closable="false"
    @on-cancel="closeModal"
  >
    <div class="upload-wrapper">
      <section v-if="isSupportCustom" class="type-wrapper">
        <RadioGroup v-model="uploadType" type="button" button-style="solid" class="radios-container">
          <Radio v-for="item in uploadTypes" :key="item.value" :label="item.value" class="item--radio">
            {{ item.label }}
          </Radio>
        </RadioGroup>
      </section>
      <section v-if="uploadType === 'file'" class="content-wrapper upload-file-wrapper">
        <UploadFile v-model="file" :accept="fileAccepts" class="upload-file"></UploadFile>
        <span class="file-name">{{ fileName }}</span>
      </section>
      <section v-if="isSupportCustom && uploadType === 'text'" class="content-wrapper code-editor-wapper">
        <Form ref="fileContentRef" :model="fileContent" :rules="rules">
          <FormItem :label="$t('upload.fileName')" prop="name">
            <i-input v-model="fileContent.name" size="large"></i-input>
          </FormItem>
          <FormItem :label="$t('upload.fileContent')" prop="content" class="form-item--code">
            <UploadFile
              :accept="textFileAccept"
              class="upload-file editor-upload-file--btn"
              @handleUpload="getFileContent"
            >
              <template v-slot:text>
                <Button type="text" class="upload-file--link">{{ $t('upload.uploadFile') }}</Button>
              </template>
            </UploadFile>
            <div class="monaco-container code-container">
              <Monaco id="file-data" v-model="fileContent.content" lang="json"></Monaco>
            </div>
          </FormItem>
        </Form>
      </section>
    </div>
    <div slot="footer">
      <Button type="text" @click="closeModal">{{ $t('common.cancel') }}</Button>
      <Button :loading="loadingUpload" type="primary" @click="submit">
        {{ $t('common.submit') }}
      </Button>
    </div>
  </Modal>
</template>

<script>
import nodeID from '@/mixins/nodeID'
import Monaco from '@/components/Monaco'
import UploadFile from './components/UploadFile.vue'
import { uploadFile } from '@/api/upload'

export default {
  name: 'UploadFileModal',

  props: {
    isSupportCustom: { type: Boolean, deafult: true },
    fileAccepts: { type: String, deafult: '-' },
    modalVisible: { type: Boolean, deafult: false },
  },

  mixins: [nodeID],

  components: { UploadFile, Monaco },

  data() {
    return {
      uploadTypes: [
        {
          value: 'file',
          label: this.$t('upload.uploadByFile'),
        },
        {
          value: 'text',
          label: this.$t('upload.uploadByContent'),
        },
      ],
      uploadType: 'file',
      file: null,
      fileContent: {
        name: '',
        content: '',
      },
      textFileAccept: 'text/plain, application/x-yaml, application/json, application/javascript',
      loadingUpload: false,
      rules: {
        name: [{ required: true, message: this.$t('upload.fileNameRequried'), trigger: 'blur' }],
        content: [{ required: true, message: this.$t('upload.fileContentRequried') }],
      },
    }
  },
  computed: {
    visible: {
      get() {
        return this.modalVisible
      },
      set(val) {
        this.$emit('update:modalVisible', val)
      },
    },

    fileName() {
      return this.file ? this.file.name : ''
    },
  },

  methods: {
    readFile(file) {
      return new Promise((resolve, reject) => {
        this.loadingUpload = true
        let content = ''
        const reader = new FileReader()
        if (typeof FileReader === 'undefined') {
          this.$Message.warning(this.$t('upload.unsupportReadOnBrower'))
          return false
        }
        reader.readAsText(file, 'UTF-8')
        reader.onerror = (err) => {
          this.loadingUpload = false
          this.$Message.warning(this.$t('upload.readFileError'))
          reject(err)
        }
        reader.onload = (ev) => {
          this.loadingUpload = false
          content = ev.target.result
          resolve(ev.target.result)
        }
        return content
      })
    },

    // get editor file content
    getFileContent(file) {
      this.readFile(file).then((res) => {
        this.fileContent.content = res
      })
    },

    closeModal() {
      this.visible = false
      this.file = null
      this.fileContent = { name: '', content: '' }
      this.$emit('clode')
    },

    // upload file
    upload(data, isFormData) {
      return new Promise((resolve, reject) => {
        this.loadingUpload = true
        uploadFile(this.nodeID, data, isFormData)
          .then((res) => {
            this.$Message.success(this.$t('common.uploadSuccess'))
            this.closeModal()
            this.$emit('reload')
            resolve(res)
          })
          .catch((err) => {
            reject(err)
          })
          .finally(() => {
            this.loadingUpload = false
          })
      })
    },

    submit() {
      if (this.uploadType === 'file') {
        if (!this.file) {
          this.$Message.warning(this.$t('upload.fileRequried'))
          return
        }
        const formData = new FormData()
        formData.append('uploadFile', this.file)
        this.upload(formData, true)
      } else {
        this.$refs.fileContentRef.validate((valid) => {
          if (!valid) {
            return
          }
          const data = {
            name: this.fileContent.name,
            content: this.fileContent.content,
          }
          this.upload(data, false)
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.type-wrapper {
  .label {
    display: inline-block;
    text-align: left;
  }
  .radios-container {
    margin-top: 10px;
    width: 100%;
    display: flex;
  }
  .item--radio {
    flex: 1;
    text-align: center;
  }
}

.content-wrapper {
  margin-top: 20px;
  .file-name {
    padding: 0 16px;
  }
}

.upload-file-wrapper {
  text-align: center;
}
.upload-wrapper {
  width: 100%;
  padding: 16px 16px 0;
}

.code-editor-wapper {
  .item-container {
    margin-bottom: 10px;
  }
  .editor-upload-file--btn {
    text-align: right;
  }
  .upload-file--link {
    display: inline-block;
    padding-right: 0;
  }
  ::v-deep .form-item--code {
    margin-bottom: 0;
  }
  .code-container,
  #monaco-file-data {
    min-height: 200px;
    width: 100%;
  }
}
</style>
